import api from "@/helpers/api";

const STORAGE_KEY = "currentPrintProfile";

// Sensitive children (signatories, bank accounts, terms) are held in-memory
// ONLY. Bank account numbers / IFSC / signature paths must never persist to
// localStorage — XSS would exfiltrate them indefinitely. The display-safe
// projection below is what survives a page reload; sensitive fields are
// re-fetched on hydrate.
//
// Identity / legal text (logo, footer, jurisdiction, declaration) lives on
// the Company master and is included in the profile DTO returned by the API
// so print components can read everything from a single store getter.
function projectForStorage(profile) {
  if (!profile) return null;
  return {
    id: profile.id,
    companyId: profile.companyId,
    accentColorHex: profile.accentColorHex,
    fontFamily: profile.fontFamily,
    paperSize: profile.paperSize,
    orientation: profile.orientation,
    marginTopMm: profile.marginTopMm,
    marginRightMm: profile.marginRightMm,
    marginBottomMm: profile.marginBottomMm,
    marginLeftMm: profile.marginLeftMm,
    headerLogoPath: profile.headerLogoPath,
    secondaryLogoPath: profile.secondaryLogoPath,
    footerText: profile.footerText,
    jurisdictionText: profile.jurisdictionText,
    jurisdictionCity: profile.jurisdictionCity,
    declarationText: profile.declarationText,
    showBankDetailsOnInvoice: profile.showBankDetailsOnInvoice,
    watermarkDraftText: profile.watermarkDraftText,
    watermarkCancelledText: profile.watermarkCancelledText,
    printTemplateVersion: profile.printTemplateVersion,
  };
}

function loadCachedProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export const state = {
  // Display-safe fields seeded synchronously from localStorage so the first
  // render after refresh has correct paper size / accent color.
  profile: loadCachedProfile(),
  // Sensitive children — populated by hydrate(), never cached.
  signatories: [],
  bankAccounts: [],
  terms: [],
  loading: false,
  loaded: false,
};

export const getters = {
  profile: (s) => s.profile,
  signatories: (s) => s.signatories,
  bankAccounts: (s) => s.bankAccounts,
  defaultBankAccount: (s) => s.bankAccounts.find((a) => a.isDefault) || s.bankAccounts[0],
  terms: (s) => s.terms,

  /**
   * Picks the active T&C version for a (voucherType, voucherDate). Mirrors
   * the backend's CompanyPrintTerms.SelectTermsFor rule: highest Version
   * where IsActive AND EffectiveFrom <= voucherDate.
   */
  termsFor: (s) => (voucherType, voucherDate) => {
    const date = voucherDate instanceof Date ? voucherDate : new Date(voucherDate);
    return s.terms
      .filter((t) => t.voucherType === voucherType && t.isActive)
      .filter((t) => new Date(t.effectiveFrom) <= date)
      .sort((a, b) => b.version - a.version)[0] || null;
  },

  isUnifiedTemplate: (s) => (s.profile?.printTemplateVersion || 1) === 2,
};

export const mutations = {
  SET_PROFILE(state, dto) {
    if (!dto) {
      state.profile = null;
      state.signatories = [];
      state.bankAccounts = [];
      state.terms = [];
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    const safe = projectForStorage(dto);
    state.profile = safe;
    state.signatories = dto.signatories || [];
    state.bankAccounts = dto.bankAccounts || [];
    state.terms = dto.terms || [];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  },
  SET_LOADING(state, flag) {
    state.loading = flag;
  },
  SET_LOADED(state, flag) {
    state.loaded = flag;
  },
};

export const actions = {
  async hydrate({ commit, state: s }) {
    if (s.loading) return;
    commit("SET_LOADING", true);
    try {
      const { data } = await api.get("/print-profile/current");
      commit("SET_PROFILE", data);
      commit("SET_LOADED", true);
    } catch (err) {
      console.warn("printProfile/hydrate failed:", err?.message || err);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async update({ commit }, payload) {
    const { data } = await api.put("/print-profile", payload);
    commit("SET_PROFILE", data);
    return data;
  },

  async upsertSignatory({ dispatch }, payload) {
    await api.put("/print-profile/signatories", payload);
    return dispatch("hydrate");
  },

  async deleteSignatory({ dispatch }, id) {
    await api.delete(`/print-profile/signatories/${id}`);
    return dispatch("hydrate");
  },

  async upsertBankAccount({ dispatch }, payload) {
    await api.put("/print-profile/bank-accounts", payload);
    return dispatch("hydrate");
  },

  async deleteBankAccount({ dispatch }, id) {
    await api.delete(`/print-profile/bank-accounts/${id}`);
    return dispatch("hydrate");
  },

  async createTermsVersion({ dispatch }, payload) {
    await api.post("/print-profile/terms", payload);
    return dispatch("hydrate");
  },

  async deactivateTerms({ dispatch }, id) {
    await api.post(`/print-profile/terms/${id}/deactivate`);
    return dispatch("hydrate");
  },

  reset({ commit }) {
    commit("SET_PROFILE", null);
    commit("SET_LOADED", false);
  },
};
